import React, { useContext, useState } from "react"
import cn from "classnames"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleRight } from "@fortawesome/pro-light-svg-icons/faAngleRight"
import { faFileAlt } from "@fortawesome/pro-light-svg-icons/faFileAlt"
import TooltipTrigger from "../TooltipTrigger/TooltipTrigger"
import { TreeItem } from "@atlaskit/tree"
import { ItemId } from "@atlaskit/tree/types"
import "./Category.scss"
import { KnowledgeBasePermittedAction } from "../../models/knowledgeBase/permission"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { useAppSelector } from "../../store/hooks"
import { selectCurrentArticle } from "../../store/knowledgeBase/selectors"
import {
    selectCurrentClientOmniUserId,
    selectDialogId,
    selectIsKnowledgeBaseSidebarOpened
} from "../../store/dialogs/selectors"
import { testId } from "../../utility/tests/testId/knowledgeBase"
import knowledgeBaseController from "../../api/controllers/knowledgeBase"
import { selectCurrentProjectId } from "../../store/projects/selectors"
import truncate from "lodash/truncate"
import { Link, useLocation } from "react-router-dom"
import debounce from "lodash/debounce"
import { definedMenuItems } from "../../utility/menu/definedMenuItems"
import { projectPath } from "../../routerPaths"
import { buildArticleActionUrl } from "../../utility/knowledgeBase/articleUrlHelper"
import { ArticleAction } from "../../models/article"
import KnowledgeBaseContext from "../KnowledgeBaseProvider/KnowledgeBaseContext"
import { ROOT_CATEGORY_CODE } from "../../utility/knowledgeBase/knowledgeBase"
import { dialogsApi } from "../../api/controllers/dialogs"

const tNamespace = "knowledgeBase:"
const tooltipDelay = { show: 450, hide: 0 }
const categoryTitleMaxLength = 40

export type OnTitleClick = (categoryItem: TreeItem, catalogCode?: string, categoryCode?: string) => void

export interface CategoryProps {
    item: TreeItem
    onExpand: (itemId: ItemId) => void
    onCollapse: (itemId: ItemId) => void
    onTitleClick?: OnTitleClick
    onCanEdit: (permittedAction?: KnowledgeBasePermittedAction) => boolean
    isDragging?: boolean
    titleClassName?: string
    selected?: boolean
    showExtensions?: boolean
    extensions?: JSX.Element[]
    isRoot?: boolean
    icon?: IconProp | ((item: TreeItem) => IconProp)
    showContentOnHover?: boolean
    isHide?: boolean
    noChildren?: boolean
    asLink?: boolean
}

export const Category: React.FC<CategoryProps> = props => {
    const {
        item,
        onExpand,
        onCollapse,
        isDragging,
        titleClassName,
        onTitleClick,
        onCanEdit,
        selected,
        showExtensions,
        extensions,
        icon,
        isRoot = false,
        showContentOnHover,
        isHide = false,
        noChildren = false,
        asLink = false
    } = props

    const { t } = useTranslation()
    const {
        id,
        data: { title = "", catalogCode, symbolCode, permittedAction }
    } = item

    const canEdit = onCanEdit(permittedAction) || item.data.isQueue

    const getToggleIcon = (
        item: TreeItem,
        onExpand: (itemId: ItemId) => void,
        onCollapse: (itemId: ItemId) => void
    ): [JSX.Element, () => void] =>
        item.isExpanded
            ? [
                  <FontAwesomeIcon
                      icon={faAngleRight}
                      size="lg"
                      className="category__arrow category__arrow_expanded"
                  />,
                  () => onCollapse(item.id)
              ]
            : [<FontAwesomeIcon icon={faAngleRight} size="lg" className="category__arrow" />, () => onExpand(item.id)]

    const getIcon = (item: TreeItem) => {
        if (noChildren) {
            return null
        }
        if (item.hasChildren) {
            const [toggleIcon, toggleCallback] = getToggleIcon(item, onExpand, onCollapse)
            return toggleIcon && toggleCallback && <CategoryIcon icon={toggleIcon} onClick={toggleCallback} />
        }

        const getIconContainer = (icon: IconProp) => (
            <CategoryIcon
                icon={
                    <FontAwesomeIcon
                        icon={icon}
                        size="lg"
                        className={cn("category__article", isHide ?? "category__article_hide")}
                    />
                }
            />
        )

        if (!icon) {
            return getIconContainer(faFileAlt)
        } else if (typeof icon === "function") {
            return getIconContainer(icon(item))
        } else {
            return getIconContainer(icon)
        }
    }

    const { url } = useContext(KnowledgeBaseContext)

    const handleTitleClick = (event: React.MouseEvent<HTMLElement>) => {
        if ((event?.ctrlKey || event?.metaKey || event?.shiftKey) && item.data?.symbolCode !== ROOT_CATEGORY_CODE) {
            return
        }
        event?.preventDefault()
        if (onTitleClick) {
            onTitleClick(item, catalogCode, symbolCode)
        }
    }

    const currentProjectId = useAppSelector(selectCurrentProjectId)
    const selectedDialogId = useAppSelector(selectDialogId)
    const currentClientOmniUserId = useAppSelector(selectCurrentClientOmniUserId)
    const strictId = selectedDialogId ?? currentClientOmniUserId ?? ""
    const { data: dialogData } = useAppSelector(dialogsApi.endpoints.getDialog.select(strictId))
    const dialogProjectId = dialogData?.Project?.Id
    const projectId = dialogProjectId || currentProjectId

    const currentArticle = useAppSelector(selectCurrentArticle)
    const isOpenedArticle: boolean = `${symbolCode}` === currentArticle?.Article?.SymbolCode

    const [articleContent, setArticleContent] = useState<string | null>(null)
    const { pathname } = useLocation()
    const isKnowledgeBaseOpen = useAppSelector(selectIsKnowledgeBaseSidebarOpened)

    const prefetchArticleContent = async () => {
        const isShowContentOnHover =
            !pathname.includes(`${projectPath}/${projectId}/${definedMenuItems.KnowledgeBase.id}`) &&
            !isKnowledgeBaseOpen &&
            item.data.symbolCode &&
            projectId &&
            articleContent === null &&
            !isRoot

        if (isShowContentOnHover) {
            const { Article } = await knowledgeBaseController.getArticle(projectId, item.data.symbolCode)
            const content = Article?.Answers?.[0].Text ?? ""
            setArticleContent(Boolean(Article?.Scenario) ? t(`${tNamespace}scenario`) : content)
        }
    }

    const debouncedFetchArticleContent = debounce(prefetchArticleContent, 300)

    const handleMouseLeave = () => {
        debouncedFetchArticleContent.cancel()
    }

    const tooltipCondition =
        (showContentOnHover && Boolean(articleContent?.length)) || title.length > categoryTitleMaxLength

    const tooltipContent = showContentOnHover && Boolean(articleContent?.length) ? articleContent : title
    const tooltipId = typeof id === "string" ? id : String(id)

    const defaultProps = {
        onMouseEnter: debouncedFetchArticleContent,
        onMouseLeave: handleMouseLeave,
        "data-testid": testId.categoryTitleLink
    }

    const Category = (
        <div
            className={cn(
                "category",
                selected || isOpenedArticle ? "category_selected" : undefined,
                showExtensions ? "category_extensions-shown" : undefined,
                isDragging ? "category__drag" : undefined,
                isRoot ? "category__root" : undefined,
                isHide && "category_hide",
                noChildren && "category_no-icon-padding"
            )}
            data-testid={testId.category}
        >
            {getIcon(item)}

            <TooltipTrigger
                id={tooltipId}
                placement={showContentOnHover ? "auto" : "top"}
                content={truncate(tooltipContent, { length: 1000, omission: "..." })}
                condition={tooltipCondition}
                delay={tooltipDelay}
            >
                <div
                    className={cn(
                        isOpenedArticle ? "category__title_isOpenedArticle" : "category__title",
                        titleClassName
                    )}
                    onClick={handleTitleClick}
                    onMouseEnter={debouncedFetchArticleContent}
                    onMouseLeave={handleMouseLeave}
                    data-testid={testId.categoryTitle}
                >
                    {title ? title : t(`${tNamespace}untitled`)}
                </div>
            </TooltipTrigger>
            {canEdit && extensions}
        </div>
    )

    if (asLink) {
        return (
            <Link
                {...defaultProps}
                to={buildArticleActionUrl(url, ArticleAction.View, symbolCode)}
                rel={"noopener noreferrer"}
                onClick={(event: React.MouseEvent<HTMLElement>) => handleTitleClick(event)}
                className="categoryLink"
            >
                {Category}
            </Link>
        )
    } else {
        return <div {...defaultProps}>{Category}</div>
    }
}

interface CategoryIconProps {
    icon: JSX.Element
    onClick?: () => void
}

const CategoryIcon: React.FC<CategoryIconProps> = props => {
    const { icon, onClick } = props

    return (
        <div
            className="category__toggle"
            onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                onClick?.()
            }}
            data-testid={testId.categoryToggleIcon}
        >
            <div className="category__toggle-icon">{icon}</div>
        </div>
    )
}
